/* ReQaf PDP gallery — scroll-snap carousel with arrow-nav thumb strip.
 *
 * Two containers:
 *   [data-reqaf-gallery-main]   — horizontal scroll-snap of slides
 *   [data-reqaf-gallery-thumbs] — horizontal scroll-snap thumb strip
 *
 * Behavior:
 *   - Click a thumb → scroll main to that slide + mark thumb active
 *   - Swipe / scroll the main → sync active thumb (debounced)
 *   - Click prev/next arrow → scroll THUMB strip left/right by ~3 thumbs
 *     (main navigation is via swipe or thumb-click, not the arrows)
 *   - Arrows fade out when at scroll boundaries
 *   - Keyboard arrow keys (← →) move the main between slides
 */

(function () {
  function init(root) {
    const main = root.querySelector('[data-reqaf-gallery-main]');
    const thumbsContainer = root.querySelector('[data-reqaf-gallery-thumbs]');
    const thumbs = Array.from(root.querySelectorAll('[data-reqaf-gallery-thumb]'));
    const slides = Array.from(root.querySelectorAll('[data-reqaf-gallery-slide]'));
    const prev = root.querySelector('[data-reqaf-gallery-prev]');
    const next = root.querySelector('[data-reqaf-gallery-next]');
    if (!main || slides.length <= 1) return;

    // Scroll main to the slide at index i.
    function scrollMainTo(i) {
      const slide = slides[i];
      if (!slide) return;
      main.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    }

    function setActiveThumb(i) {
      thumbs.forEach((t, idx) => {
        const active = idx === i;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      // Scroll the thumb strip horizontally via manual scrollLeft (not
      // scrollIntoView, which still triggers vertical page scroll via
      // `block: 'nearest'` when the strip itself is off-viewport).
      const activeEl = thumbs[i];
      if (activeEl && thumbsContainer) {
        const containerRect = thumbsContainer.getBoundingClientRect();
        const thumbRect = activeEl.getBoundingClientRect();
        if (thumbRect.left < containerRect.left || thumbRect.right > containerRect.right) {
          const thumbLeftInContainer = thumbRect.left - containerRect.left + thumbsContainer.scrollLeft;
          const targetLeft = thumbLeftInContainer - thumbsContainer.clientWidth / 2 + activeEl.offsetWidth / 2;
          thumbsContainer.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
        }
      }
    }

    // Thumb click → scroll main + mark active.
    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => {
        scrollMainTo(i);
        setActiveThumb(i);
      });
    });

    // Main scroll listener — debounced, syncs active thumb to the slide
    // whose center is closest to the container's center.
    let scrollTimer;
    main.addEventListener('scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        const center = main.scrollLeft + main.clientWidth / 2;
        for (let i = 0; i < slides.length; i++) {
          const s = slides[i];
          if (center >= s.offsetLeft && center < s.offsetLeft + s.offsetWidth) {
            setActiveThumb(i);
            break;
          }
        }
      }, 80);
    }, { passive: true });

    // Thumb-strip arrows — scroll THUMBS (not main) by ~3 thumbs' width.
    // Main navigation happens via thumb click or swipe.
    function stepSize() {
      const first = thumbs[0];
      return first ? first.offsetWidth + 8 : (thumbsContainer ? thumbsContainer.clientWidth / 3 : 100);
    }
    function updateArrowState() {
      if (!thumbsContainer || !prev || !next) return;
      const atStart = thumbsContainer.scrollLeft <= 1;
      const atEnd = thumbsContainer.scrollLeft + thumbsContainer.clientWidth >= thumbsContainer.scrollWidth - 1;
      prev.disabled = atStart;
      next.disabled = atEnd;
    }
    if (prev) prev.addEventListener('click', () => {
      if (thumbsContainer) thumbsContainer.scrollBy({ left: -stepSize() * 3, behavior: 'smooth' });
    });
    if (next) next.addEventListener('click', () => {
      if (thumbsContainer) thumbsContainer.scrollBy({ left: stepSize() * 3, behavior: 'smooth' });
    });
    if (thumbsContainer) {
      thumbsContainer.addEventListener('scroll', updateArrowState, { passive: true });
    }

    // Keyboard arrows on the gallery move the main between slides.
    root.addEventListener('keydown', (e) => {
      const activeIdx = thumbs.findIndex((t) => t.classList.contains('is-active'));
      if (e.key === 'ArrowLeft' && activeIdx > 0) {
        scrollMainTo(activeIdx - 1);
        setActiveThumb(activeIdx - 1);
        e.preventDefault();
      }
      if (e.key === 'ArrowRight' && activeIdx < slides.length - 1) {
        scrollMainTo(activeIdx + 1);
        setActiveThumb(activeIdx + 1);
        e.preventDefault();
      }
    });

    // Initial arrow state.
    requestAnimationFrame(updateArrowState);
  }

  function boot() {
    document.querySelectorAll('[data-reqaf-gallery]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
